import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, LabelList,
} from 'recharts';
import AccuracyOverTime from './AccuracyOverTime';

type TestData = {
    testSections: any[];
    configTestSections: any[];
    testName: string;
    testNumber: number;
    testType: string;
    testDate: string;
    avgScore: number | null;
    avgCompositeScore: number | null;
    avgStemScore: number | null;
    minScore: number | null;
    maxScore: number | null;
    bestScore: number | null;
    lowestScore: number | null;
    takenNum: number;
    assignedNum: number;
    avgCorrectPercent: { [subject: string]: { [category: string]: { numCorrect: number; numQuestions: number } } };
};

type PerformanceHistoryProps = {
    testType: string;
    SAT: TestData[];
    PSAT: TestData[];
    ACT: TestData[];
    SHSAT: TestData[];
    TSIA2?: TestData[];
};

const RW_SUBJECTS = ['Reading and Writing', 'English Language Arts', 'TSIA2 ELAR'];
const MATH_SUBJECTS = ['Math', 'Mathematics', 'TSIA2 Mathematics'];
const CAT_COLORS = ['#0575a2', '#00a445', '#f2994a', '#d34053', '#8b5cf6', '#0ea5e9', '#e0529c', '#b3631f'];

// 'TSIA2 ' is stripped first so 'TSIA2 Mathematics' lands on 'Math' like the
// other math subjects rather than on 'TSIA2 Math'.
const displaySubject = (s: string) =>
    (s || '').replace('TSIA2 ', '').replace('Reading and Writing', 'R/W').replace('ACT ', '').replace('Mathematics', 'Math');

// Section-label text colors, matching the Focus Areas section pills so R/W, Math,
// English, Reading and Science read the same color everywhere. Keyed by the
// displaySubject() output; non-section labels (Class avg, Composite, STEM, Tests
// taken) aren't in the map and keep the default gray.
const SECTION_LABEL_COLOR: { [label: string]: string } = {
    'R/W': '#1f7a44',
    'ELA': '#1f7a44',
    'ELAR': '#1f7a44',
    'English Language Arts': '#1f7a44',
    'English': '#1f7a44',
    'Math': '#b3631f',
    'Reading': '#b3201f',
    'Science': '#1f5fb3',
};
const labelColor = (label: string) => SECTION_LABEL_COLOR[label];

// Compact axis label for a test. Handles letter-prefixed numbers like
// "DSAT Real Test R03" (-> "Test R03"); the old /Test\s\d+/ missed those and
// fell back to the full name, which overflowed and collided with neighbours.
const shortTestName = (name: string) =>
    (name || '').match(/Test\s*[A-Za-z]?\d+/)?.[0] || (name || '').replace('Digital ', '');

const mean = (nums: number[]) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null);

const getTestSectionLabel = (sections: string[]) => {
    if (!Array.isArray(sections)) return '';
    const map = {
        'Reading and Writing': 'RW', 'Math': 'M', 'ACT English': 'E', 'ACT Mathematics': 'M',
        'ACT Reading': 'R', 'ACT Science': 'S', 'English Language Arts': 'ELA', 'Mathematics': 'M',
        'TSIA2 ELAR': 'ELAR', 'TSIA2 Mathematics': 'M',
    };
    const labels = sections.map(s => map[s]).filter(Boolean);
    const unique = Array.from(new Set(labels));
    // A full-form assignment needs no suffix; only a configured subset does.
    // No recognised sections at all must not render an empty "()".
    if (unique.length === 0) return '';
    if (unique.includes('RW') && unique.includes('M') && unique.length === 2) return '';
    const actSet = new Set(['E', 'M', 'R', 'S']);
    if (unique.length === 4 && unique.every(l => actSet.has(l))) return '';
    if (unique.includes('ELA') && unique.includes('M') && unique.length === 2) return '';
    if (unique.includes('ELAR') && unique.includes('M') && unique.length === 2) return '';
    return `(${unique.join(', ')})`;
};

// One category line chart. Legend items toggle their line on/off (per-chart state).
function CategoryChart({ title, data, categories }: { title: string; data: any[]; categories: string[] }) {
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const toggle = (c: string) =>
        setHidden(prev => {
            const next = new Set(prev);
            next.has(c) ? next.delete(c) : next.add(c);
            return next;
        });

    return (
        <div className="cdr__card">
            <div className="cdr__card-head">
                <div>
                    <div className="cdr__card-title">{title} — category % correct</div>
                    <div className="cdr__card-sub">Class average % correct per category over recent tests · click a category to show or hide</div>
                </div>
                <div className="cdr__chart-legend">
                    {categories.map((c, i) => {
                        const off = hidden.has(c);
                        return (
                            <button
                                type="button"
                                key={c}
                                className={`cdr__legend-btn${off ? ' off' : ''}`}
                                onClick={() => toggle(c)}
                                title={off ? `Show ${c}` : `Hide ${c}`}
                            >
                                <i style={{ background: off ? '#d0d5dd' : CAT_COLORS[i % CAT_COLORS.length] }} />
                                {c}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 16, right: 24, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="#EAECF0" />
                        <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: '#EAECF0' }} fontSize={12} />
                        <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tickLine={false} axisLine={false} fontSize={12} width={44} />
                        <Tooltip formatter={(v: any) => `${v}%`} />
                        {categories.map((c, i) =>
                            hidden.has(c) ? null : (
                                <Line
                                    key={c}
                                    type="linear"
                                    dataKey={c}
                                    stroke={CAT_COLORS[i % CAT_COLORS.length]}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    activeDot={{ r: 5 }}
                                    connectNulls
                                />
                            )
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function PerformanceHistory({ testType, SAT, PSAT, ACT, SHSAT, TSIA2 = [] }: PerformanceHistoryProps) {
    const [testData, setTestData] = useState<TestData[]>([]);
    const [categorySections, setCategorySections] = useState<{ [subject: string]: { [cat: string]: number[] } }>({});
    const [progress, setProgress] = useState<any[]>([]);

    // A test can be assigned to a class more than once (duplicate GroupClassTest
    // rows), which the overview emits as repeated, identical entries. Collapse by
    // test name so charts don't show duplicate bars/points — keep the entry with
    // the most completions (ties resolve to the first, i.e. most recent).
    const dedupeByName = (arr: TestData[]) => {
        const byName = new Map<string, TestData>();
        (arr || []).forEach(t => {
            const cur = byName.get(t.testName);
            if (!cur || (t.takenNum || 0) > (cur.takenNum || 0)) byName.set(t.testName, t);
        });
        // preserve original order (newest-first)
        const seen = new Set<string>();
        return (arr || []).filter(t => {
            if (seen.has(t.testName)) return false;
            seen.add(t.testName);
            return true;
        }).map(t => byName.get(t.testName)!);
    };

    useEffect(() => {
        if (testType === 'SAT') setTestData(dedupeByName(SAT || []));
        else if (testType === 'PSAT') setTestData(dedupeByName(PSAT || []));
        else if (testType === 'ACT') setTestData(dedupeByName(ACT || []));
        else if (testType === 'SHSAT') setTestData(dedupeByName(SHSAT || []));
        else if (testType === 'TSIA2') setTestData(dedupeByName(TSIA2 || []));
    }, [testType, SAT, PSAT, ACT, SHSAT, TSIA2]);

    useEffect(() => {
        const result: { [subject: string]: { [cat: string]: number[] } } = {};
        const prog: any[] = [];

        // oldest -> newest so charts read left-to-right chronologically
        testData.slice().reverse().forEach(test => {
            const { avgCorrectPercent } = test;
            Object.entries(avgCorrectPercent || {}).forEach(([subject, categories]) => {
                if (!result[subject]) result[subject] = {};
                Object.keys(categories).forEach(category => {
                    if (!result[subject][category]) result[subject][category] = [];
                });
            });
            if (test.takenNum) {
                Object.entries(avgCorrectPercent || {}).forEach(([subject, categories]) => {
                    Object.entries(categories).forEach(([category, info]: any) => {
                        const pct = info.numQuestions > 0 ? (info.numCorrect / info.numQuestions) * 100 : 0;
                        result[subject][category].push(Math.round(pct));
                    });
                });
            }

            const subjectScores: { [s: string]: number | null } = {};
            test.testSections.forEach(section => {
                subjectScores[section.subject] = section.sectionAVGScore;
            });
            prog.push({
                testName: shortTestName(test.testName),
                assignedDate: test.testDate,
                takenNum: test.takenNum,
                avgTotalScore: test.avgScore,
                avgCompositeScore: test.avgCompositeScore,
                avgStemScore: test.avgStemScore,
                ...subjectScores,
            });
        });

        setCategorySections(result);
        setProgress(prog);
    }, [testData]);

    // "Latest" for the summary/KPIs is the most recent test that actually has
    // completions — a freshly assigned-but-untaken test (takenNum === 0) would
    // otherwise blank out the card even though older completed results exist.
    // Fall back to the newest assigned test only when nothing has been taken.
    const takenTests = useMemo(() => testData.filter(t => t.takenNum > 0), [testData]);
    const latest = takenTests[0] || testData[0];
    const prev = takenTests[0] ? takenTests[1] : undefined;
    const isACT = testType === 'ACT';
    const isSHSAT = testType === 'SHSAT';

    const fmtDelta = (cur: any, prior: any, suffix = '') => {
        if (cur == null || prior == null) return null;
        const d = Math.round(cur - prior);
        if (d === 0) return { txt: 'No change', cls: 'flat' };
        return { txt: `${d > 0 ? '+' : ''}${d}${suffix} vs prev`, cls: d > 0 ? 'pos' : 'neg' };
    };

    const sectionScore = (t: TestData | undefined, subject: string) =>
        t?.testSections?.find(s => s.subject === subject)?.sectionAVGScore ?? null;

    // KPI strip = class averages across ALL completed tests of this type. This is
    // intentionally distinct from the Latest Test card above (which shows only the
    // most recent test) — these are the class's all-time averages.
    const kpis = useMemo(() => {
        if (!takenTests.length) return [];
        const out: { label: string; val: any; total?: number }[] = [];
        if (isACT) {
            const comp = mean(takenTests.map(t => t.avgCompositeScore).filter((v): v is number => v != null));
            const stem = mean(takenTests.map(t => t.avgStemScore).filter((v): v is number => v != null));
            if (comp != null) out.push({ label: 'Composite', val: Math.round(comp) });
            if (stem != null) out.push({ label: 'STEM', val: Math.round(stem) });
        } else {
            const cls = mean(takenTests.map(t => t.avgScore).filter((v): v is number => v != null));
            // SHSAT scores are raw question counts — show "/ total questions" for context.
            const clsTotal = isSHSAT ? Math.max(0, ...takenTests.map(t => t.maxScore ?? 0)) : undefined;
            out.push({ label: 'Class avg', val: cls != null ? Math.round(cls) : '-', total: clsTotal });
            // union of sections across all taken tests, in first-seen order
            const subjects: string[] = [];
            takenTests.forEach(t => t.testSections.forEach(s => { if (!subjects.includes(s.subject)) subjects.push(s.subject); }));
            subjects.forEach(subj => {
                const m = mean(takenTests
                    .map(t => t.testSections.find(s => s.subject === subj)?.sectionAVGScore)
                    .filter((v): v is number => v != null));
                const subjTotal = isSHSAT
                    ? Math.max(0, ...takenTests.map(t => t.testSections.find(s => s.subject === subj)?.maxScore ?? 0))
                    : undefined;
                if (m != null) out.push({ label: displaySubject(subj), val: Math.round(m), total: subjTotal });
            });
        }
        out.push({ label: 'Tests taken', val: takenTests.length });
        return out;
    }, [takenTests, isACT, isSHSAT]);

    // ACT only: a second KPI row with per-section class averages across ALL taken
    // tests (English / Math / Reading / Science), shown under the Composite/STEM/
    // Tests-taken row. Non-ACT types already list their sections in the main strip.
    const sectionKpis = useMemo(() => {
        if (!isACT || !takenTests.length) return [];
        const subjects: string[] = [];
        takenTests.forEach(t => t.testSections.forEach(s => { if (!subjects.includes(s.subject)) subjects.push(s.subject); }));
        const out: { label: string; val: any }[] = [];
        subjects.forEach(subj => {
            const m = mean(takenTests
                .map(t => t.testSections.find(s => s.subject === subj)?.sectionAVGScore)
                .filter((v): v is number => v != null));
            if (m != null) out.push({ label: displaySubject(subj), val: Math.round(m) });
        });
        return out;
    }, [isACT, takenTests]);

    // Bar chart: which section keys to stack (SAT/PSAT/SHSAT)
    const rwKey = latest?.testSections?.find(s => RW_SUBJECTS.includes(s.subject))?.subject;
    const mathKey = latest?.testSections?.find(s => MATH_SUBJECTS.includes(s.subject))?.subject;

    // TSIA2 is scored as a flat percent, so its axis is 0 to 100.
    const yDomain: [number, number | string] = isACT ? [0, 36] : testType === 'SHSAT' ? [0, 'auto'] : testType === 'TSIA2' ? [0, 100] : [0, 1600];

    // Class-average chart shows only tests that actually have scores — an
    // assigned-but-untaken test (takenNum === 0) would otherwise render an empty
    // bar and clutter the timeline.
    const scoreProgress = useMemo(() => progress.filter(p => p.takenNum > 0), [progress]);

    // X-axis tick: test name with the date it was assigned shown underneath.
    const renderTestTick = ({ x, y, payload }: any) => {
        const row = scoreProgress.find(p => p.testName === payload.value);
        const dateStr = row?.assignedDate ? moment(row.assignedDate).format('MMM D, YYYY') : '';
        // Rotate the labels so long/variable-length test names never collide
        // with their neighbours (centered horizontal text overlapped once names
        // exceeded the bar width). Name on top, assigned date on a parallel line.
        return (
            <g transform={`translate(${x},${y})`}>
                <g transform="rotate(-28)">
                    <text x={0} y={0} dy={6} textAnchor="end" fontSize={11} fill="#313131">{payload.value}</text>
                    {dateStr ? (
                        <text x={0} y={0} dy={18} textAnchor="end" fontSize={9} fill="#98A2B3">{dateStr}</text>
                    ) : null}
                </g>
            </g>
        );
    };

    const lineDataFor = (cats: { [cat: string]: number[] }) => {
        const takenNames = progress.filter(p => p.takenNum).map(p => p.testName);
        const len = Math.max(0, ...Object.values(cats).map(a => a.length));
        return Array.from({ length: len }, (_, i) => {
            const row: any = { name: takenNames[i] || `Test ${i + 1}` };
            Object.entries(cats).forEach(([c, arr]) => { row[c] = arr[i]; });
            return row;
        });
    };

    if (!latest) {
        return (
            <div className="cdr__tab-content">
                <div className="cdr__empty">No test data for this test type yet.</div>
            </div>
        );
    }

    return (
        <div className="cdr__tab-content">
            {/* Latest test summary */}
            <div className="cdr__card">
                <div className="cdr__card-sub">Latest Test</div>
                <div className="cdr__summary-name">
                    {latest.testName} {getTestSectionLabel(latest.configTestSections)}
                </div>
                <div className="cdr__summary-date">
                    {moment(latest.testDate).format('MMMM D, YYYY')} · {latest.takenNum} of {latest.assignedNum} students completed
                </div>
                <div className="cdr__stat-grid">
                    {isACT ? (
                        <>
                            {latest.avgCompositeScore != null && (
                                <div className="cdr__stat">
                                    <div className="cdr__stat-label">Composite</div>
                                    <div className="cdr__stat-val">{Math.round(latest.avgCompositeScore)}</div>
                                </div>
                            )}
                            {latest.avgStemScore != null && (
                                <div className="cdr__stat cdr__stat--divider">
                                    <div className="cdr__stat-label">STEM</div>
                                    <div className="cdr__stat-val">{Math.round(latest.avgStemScore)}</div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="cdr__stat">
                            <div className="cdr__stat-label">Class avg</div>
                            <div className="cdr__stat-val">
                                {latest.avgScore != null ? Math.round(latest.avgScore) : '-'}
                                {isSHSAT && latest.maxScore != null ? ` / ${Math.round(latest.maxScore)}` : ''}
                            </div>
                            {!isSHSAT && latest.minScore != null && latest.maxScore != null && (
                                <div className="cdr__stat-range">{Math.round(latest.minScore)} to {Math.round(latest.maxScore)}</div>
                            )}
                        </div>
                    )}
                    {latest.testSections.map((sec, i) =>
                        sec.sectionAVGScore != null ? (
                            <div className="cdr__stat cdr__stat--divider" key={i}>
                                <div className="cdr__stat-label" style={{ color: labelColor(displaySubject(sec.subject)) }}>{displaySubject(sec.subject)}</div>
                                <div className="cdr__stat-val cdr__stat-val--sm">
                                    {Math.round(sec.sectionAVGScore)}
                                    {isSHSAT && sec.maxScore != null ? ` / ${Math.round(sec.maxScore)}` : ''}
                                </div>
                            </div>
                        ) : null
                    )}
                </div>
            </div>

            {/* KPI strip — class averages across all completed tests of this type */}
            {kpis.length > 0 && (
                <div className="cdr__kpi-caption">
                    Across all tests taken · class average
                </div>
            )}
            <div className="cdr__kpis">
                {kpis.map((k, i) => (
                    <div className="cdr__kpi" key={i}>
                        <div className="cdr__kpi-label" style={{ color: labelColor(k.label) }}>{k.label}</div>
                        <div className="cdr__kpi-val">{k.val}{k.total ? ` / ${k.total}` : ''}</div>
                    </div>
                ))}
            </div>
            {/* ACT: per-section class averages across all taken tests, one row below */}
            {sectionKpis.length > 0 && (
                <div className="cdr__kpis">
                    {sectionKpis.map((k, i) => (
                        <div className="cdr__kpi" key={i}>
                            <div className="cdr__kpi-label" style={{ color: labelColor(k.label) }}>{k.label}</div>
                            <div className="cdr__kpi-val">{k.val}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Class average over time */}
            <div className="cdr__card">
                <div className="cdr__card-head">
                    <div>
                        <div className="cdr__card-title">Class average</div>
                        <div className="cdr__card-sub">Average score per test over time</div>
                    </div>
                    <div className="cdr__chart-legend">
                        {isACT ? (
                            <>
                                <span><i style={{ background: '#0575a2' }} />Composite</span>
                                <span><i style={{ background: '#00a445' }} />STEM</span>
                            </>
                        ) : (
                            <>
                                <span><i style={{ background: '#7FB347' }} />{rwKey ? displaySubject(rwKey) : 'R/W'}</span>
                                <span><i style={{ background: '#0575a2' }} />Math</span>
                            </>
                        )}
                    </div>
                </div>
                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={scoreProgress} margin={{ top: 24, right: 20, left: 0, bottom: 16 }}>
                            <CartesianGrid strokeDasharray="3 4" vertical={false} stroke="#EAECF0" />
                            <XAxis dataKey="testName" tickLine={false} axisLine={{ stroke: '#EAECF0' }} fontSize={12} interval={0} height={72} tick={renderTestTick} />
                            <YAxis domain={yDomain} tickLine={false} axisLine={false} fontSize={12} width={48} />
                            <Tooltip cursor={{ fill: '#0575a209' }} />
                            {isACT ? (
                                <>
                                    <Bar dataKey="avgCompositeScore" name="Composite" fill="#0575a2" radius={[3, 3, 0, 0]} maxBarSize={64}>
                                        <LabelList dataKey="avgCompositeScore" position="top" fontSize={12} fill="#313131" />
                                    </Bar>
                                    <Bar dataKey="avgStemScore" name="STEM" fill="#00a445" radius={[3, 3, 0, 0]} maxBarSize={64} />
                                </>
                            ) : (
                                <>
                                    {rwKey && <Bar dataKey={rwKey} name={displaySubject(rwKey)} stackId="a" fill="#7FB347" maxBarSize={72} />}
                                    {mathKey && (
                                        <Bar dataKey={mathKey} name="Math" stackId="a" fill="#0575a2" radius={[3, 3, 0, 0]} maxBarSize={72}>
                                            <LabelList dataKey="avgTotalScore" position="top" fontSize={12} fontWeight={700} fill="#313131" />
                                        </Bar>
                                    )}
                                </>
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Accuracy over time — class-average % correct per domain/skill across tests */}
            <AccuracyOverTime tests={testData} />
        </div>
    );
}

export default PerformanceHistory;
